import { useState } from 'react';
import { BinaryButtonGroup } from '@hungryroot/app-mhr';
import { useExperiment } from '@hungryroot/experiments';
import { NumberInput, DisplayVariant, ButtonsGroup } from '@hungryroot/pho';
import {
  DINNER_CONTAINER_ID,
  MAX_DINNER_RECIPES_4_SERVINGS,
  MAX_SERVING_SIZE,
  MIN_SERVING_SIZE,
  getPricePerServing,
  THREE_FIVE_SERVING_SIZE_PRICING_VERSION,
} from '@hungryroot/plan';
import { Heading, Paragraph } from '@hungryroot/ramen';
import {
  type CustomerContainerPriceOptions,
  type ServingSize,
} from '@hungryroot/rest';
import { segmentAnalytics as analytics } from '@hungryroot/segment';
import { StyledSheetOrModal } from '../StyledSheetOrModal/StyledSheetOrModal';
import styles from './EditContainerQuantityModal.module.css';

const getMaxQuantityForServingSize = (
  priceOptions: CustomerContainerPriceOptions | undefined,
  servingSize: ServingSize
): number => {
  if (!priceOptions) {
    return 0;
  }

  const optionsForServingSize = priceOptions.pricing_options.filter(
    (option) => option.RECIPE_QUANTITY?.serving_size === servingSize
  );

  // if no options match the serving size, return 0 as a safe fallback
  if (optionsForServingSize.length === 0) {
    return 0;
  }

  return Math.max(
    ...optionsForServingSize.map(
      (option) => option.RECIPE_QUANTITY?.quantity || 0
    )
  );
};

interface EditContainerQuantityModalProps {
  isOpen: boolean;
  onSubmit: (value: number, servingSize: ServingSize) => void;
  onCancel(): void;
  initialValue: number;
  minQuantity: number;
  isLoading?: boolean;
  isSubmitting?: boolean;
  confirmButtonText: string;
  inputLabel: string;
  modalHeading: string;
  modalBody: string;
  servingSize: ServingSize;
  onServingSizeChange?: (value: ServingSize) => void;
  priceOptions?: CustomerContainerPriceOptions;
  containerName: string;
  containerId: number;
  pricingVersion: number;
}

export function EditContainerQuantityModal({
  pricingVersion,
  isOpen,
  onSubmit,
  onCancel,
  isLoading,
  isSubmitting,
  initialValue,
  minQuantity,
  confirmButtonText,
  inputLabel,
  modalHeading,
  modalBody,
  servingSize,
  onServingSizeChange,
  priceOptions,
  containerName,
  containerId,
}: EditContainerQuantityModalProps) {
  const [quantity, setQuantity] = useState(initialValue);

  const singlePricePerCreditVariant = useExperiment(165);

  const isSinglePricePerCreditVariant =
    singlePricePerCreditVariant !== undefined &&
    singlePricePerCreditVariant > 0;

  // guard against undefined priceOptions
  if (!priceOptions) {
    return null;
  }

  const { price_per_serving, max_price_per_serving } =
    getPricePerServing(quantity, priceOptions, servingSize) || {};

  const effectivePricePerServing =
    quantity === 0 && price_per_serving === undefined ? 0 : price_per_serving;

  const showServingSizeToggle = onServingSizeChange !== undefined;

  const maxQuantityForServingSize = getMaxQuantityForServingSize(
    priceOptions,
    servingSize
  );

  return (
    <StyledSheetOrModal
      testid="edit-recipe-number"
      onCancel={onCancel}
      onAdd={() => {
        if (containerId === DINNER_CONTAINER_ID) {
          analytics.track('Serving Size Update', {
            button_name: 'Update',
          });
        }

        onSubmit(quantity, servingSize);
      }}
      bodyText={modalBody}
      heading={modalHeading}
      confirmButtonText={confirmButtonText}
      ariaLabel="Edit # of servings per week"
      isOpen={isOpen}
      isSubmitting={isSubmitting}
    >
      <div>
        <NumberInput
          isLoading={isLoading}
          className={styles.row}
          buttonsClassName={styles['number-input']}
          label={inputLabel}
          value={quantity}
          minValue={minQuantity}
          maxValue={maxQuantityForServingSize}
          displayVariant={DisplayVariant.Oval}
          onChange={(next) => {
            analytics.track('Container Servings Quantity', {
              quantity_type: `${quantity - next > 0 ? 'decrease' : 'increase'}`,
              container_name: containerName,
              container_id: containerId,
            });
            setQuantity(next);
          }}
        />

        {showServingSizeToggle && (
          <div className={styles.row}>
            <Heading variant="H6">Servings</Heading>

            {pricingVersion === THREE_FIVE_SERVING_SIZE_PRICING_VERSION ? (
              <ButtonsGroup
                handleClick={(next) => {
                  const maxQuantityForNewServingSize =
                    getMaxQuantityForServingSize(
                      priceOptions,
                      next as ServingSize
                    );

                  // if current quantity is greater than the max available for the new serving size,
                  // set quantity to the max available for that serving size
                  if (quantity > maxQuantityForNewServingSize) {
                    setQuantity(maxQuantityForNewServingSize);
                  }

                  analytics.track('Serving Size Update', {
                    button_name: next,
                  });

                  onServingSizeChange(next as ServingSize);
                }}
                selected={servingSize}
                type="large-rounded"
                // make the options dynamic based on pricing options from API
                options={Object.values(
                  priceOptions.pricing_options.reduce(
                    (acc, option) => {
                      const recipeQuantity = option.RECIPE_QUANTITY;
                      const label = recipeQuantity?.serving_size?.toString();
                      const value = recipeQuantity?.serving_size;

                      if (value !== undefined && label) {
                        acc[value] = { label, value };
                      }
                      return acc;
                    },
                    {} as Record<number, { label: string; value: number }>
                  )
                ).sort((a, b) => a.value - b.value)}
              />
            ) : (
              <BinaryButtonGroup
                handleClick={(next) => {
                  if (
                    next === MAX_SERVING_SIZE &&
                    quantity > MAX_DINNER_RECIPES_4_SERVINGS
                  ) {
                    setQuantity(MAX_DINNER_RECIPES_4_SERVINGS);
                  }

                  analytics.track('Serving Size Update', {
                    button_name: next,
                  });

                  onServingSizeChange(next);
                }}
                selected={servingSize}
                buttonOptionsLeft={{
                  label: MIN_SERVING_SIZE.toString(),
                  value: MIN_SERVING_SIZE,
                }}
                buttonOptionsRight={{
                  label: MAX_SERVING_SIZE.toString(),
                  value: MAX_SERVING_SIZE,
                }}
              />
            )}
          </div>
        )}
        {effectivePricePerServing !== undefined && (
          <div className={styles.subtotal}>
            <Heading variant="H6">Subtotal</Heading>
            <Paragraph>
              {showServingSizeToggle && (
                <span className={styles['total-servings']}>
                  {quantity * servingSize} Servings
                </span>
              )}
              {!isSinglePricePerCreditVariant &&
                max_price_per_serving !== undefined &&
                effectivePricePerServing !== 0 &&
                effectivePricePerServing !== max_price_per_serving && (
                  <>
                    <span className={styles.strikethrough}>
                      {`$${max_price_per_serving}`}
                    </span>{' '}
                  </>
                )}
              {`$${effectivePricePerServing}/serving`}
            </Paragraph>
          </div>
        )}
      </div>
    </StyledSheetOrModal>
  );
}
